import { fetch, WaveformData } from 'global';
import fileExtension from '../../../utils/file-extension';
import environment from '../../../player/utils/environment';

const WAVEFORM_SUPPORTED = !!(WaveformData && !environment.isMobile);

class WaveformNotSupportedError extends Error {
  constructor () {
    super('Waveforms not supported, please ensure the BBC WaveformData library has been loaded from https://github.com/bbc/waveform-data.js');
  }
}

class WaveformExtensionError extends Error {
  constructor (ext) {
    super(`Unrecognized waveform extension '${ext}', expected one of 'json' or 'dat'`);
  }
}

class WaveformLoadError extends Error {
  constructor (url) {
    super(`Unable to load waveform from '${url}'`);
  }
}

export default class WaveformLoader {
  /**
   * Load waveform from a .dat url
   *
   * @param  {String} url
   * @return {Promise}
   */
  async loadFromDatFile (url) {
    if (!WAVEFORM_SUPPORTED) {
      throw new WaveformNotSupportedError();
    }

    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new WaveformLoadError(url);
    }

    const arrayBuffer = await response.arrayBuffer();

    return WaveformData.create(arrayBuffer);
  }

  /**
   * Load waveform from a .json url
   *
   * @param  {String} url
   * @return {Promise}
   */
  async loadFromJsonFile (url) {
    if (!WAVEFORM_SUPPORTED) {
      throw new WaveformNotSupportedError();
    }

    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new WaveformLoadError(url);
    }

    const json = await response.json();

    return WaveformData.create(json);
  }

  /**
   * Load a waveform from a remote url
   *
   * @param  {String} url
   * @return {Promise}
   */
  async load (url) {
    if (!WAVEFORM_SUPPORTED) {
      throw new WaveformNotSupportedError();
    }

    const ext = fileExtension(url);

    switch (ext) {
      case 'dat': return await this.loadFromDatFile(url);
      case 'json': return await this.loadFromJsonFile(url);
      default: throw new WaveformExtensionError(ext);
    }
  }
}
